/**
 * Helper components extracted from FuneralShow.tsx for reuse
 * in the redesigned FuneralShowSidebar and other new components.
 */
import React, { Fragment, useState, useEffect } from 'react';
import { useFocusAnchor } from '../../lib/focusAnchor';
import {
  useRecordContext,
  ReferenceField,
  TextField,
  FunctionField,
  SimpleForm,
  useDataProvider,
  useGetList,
  useGetMany,
  useRefresh,
  TextFieldProps,
} from 'react-admin';
import { EditInDialogButton } from '@react-admin/ra-form-layout';
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Truck,
  Hand,
  Store,
  Pencil,
  ChevronDown,
  AlertTriangle,
  Plus,
  Check,
} from 'lucide-react';
import { useNotify } from 'react-admin';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Funeral,
  Partner,
  ReviewRequest,
  DeliveryAddress,
  CustomerEventType,
} from '../../types';
import { partnerMatchesRegion } from '../../lib/partners';
import { getToken, getRefreshToken } from '../../authentication/authProvider';
import { URN_ITEMS } from 'paperman-contract-defaults';
import { UrnOrderDialog } from './UrnOrderDialog';
import { DeliveryEditButton } from './DeliveryEditDialog';
import { ClientSaveToolbar } from '../../clients/ClientSaveToolbar';
import ClientEditBase from '../../clients/ClientEditBase';
import moment from 'moment';
import { isPickupMethod, PICKUP_DATE_TBD } from '../../deliveries/pickupDateTbd';
import { PickupDayRuleNote } from '../../deliveries/PickupDayRuleNote';
import { pickupDayTooEarly } from '../../deliveries/pickupDayRule';
import { useRemainsPlan } from '../../deliveries/remainsApi';

// ─── AircallButton ───────────────────────────────────────────────────────────

export const AircallButton = ({ phoneNumber }: { phoneNumber: string }) => {
  if (!phoneNumber) return null;

  const handleAircallClick = () => {
    window.open(`aircall:${phoneNumber}`, '_self');
  };

  return (
    <IconButton
      onClick={handleAircallClick}
      size="small"
      sx={{
        ml: 1,
        '& img': {
          width: 20,
          height: 20,
        },
      }}
      title={`Call ${phoneNumber}`}
    >
      <img src="/aircall.png" alt="Call" />
    </IconButton>
  );
};

// ─── CopyAppLinkButton ──────────────────────────────────────────────────────

export const CopyAppLinkButton = () => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();

  if (!record?.uuid) return null;

  const handleCopyLink = async () => {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    const baseUrl = isLocalhost
      ? 'http://localhost:3010'
      : 'https://cleocremation.com';
    const appLink = `${baseUrl}/app/${(record as any).uuid}`;
    try {
      await navigator.clipboard.writeText(appLink);
      notify('App link copied to clipboard!', { type: 'success' });
    } catch (error) {
      notify('Failed to copy link', { type: 'error' });
    }
  };

  return (
    <IconButton
      onClick={handleCopyLink}
      size="small"
      sx={{ ml: 1 }}
      title="Copy app link"
    >
      <ContentCopyIcon />
    </IconButton>
  );
};

// ─── RegionChip ──────────────────────────────────────────────────────────────

export const RegionChip = () => {
  const record = useRecordContext<Funeral>();

  if (!record?.region) return null;

  const isQuebec = record.region === 'quebec';
  const regionLabel = isQuebec ? 'Quebec' : 'Ontario';
  const bgColor = isQuebec ? '#4169E1' : '#DC143C';

  return (
    <Chip
      label={regionLabel}
      sx={{
        backgroundColor: bgColor,
        color: 'white',
        fontWeight: 600,
        fontSize: '0.9rem',
        height: '32px',
        '& .MuiChip-label': {
          px: 2,
        },
      }}
    />
  );
};

// ─── WebChip ─────────────────────────────────────────────────────────────────

export const WebChip = () => {
  const record = useRecordContext<Funeral>();

  let text = 'Not Live on Website';
  let color = '#ebebeb';

  if (record.is_live) {
    text = 'Live on Website';
    color = '#fef5e4';
  }

  return (
    <Chip label={text} sx={{ marginLeft: '10px', backgroundColor: color }} />
  );
};

// ─── ReviewRequestStatus ─────────────────────────────────────────────────────

export const ReviewRequestStatus = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [reviewRequest, setReviewRequest] = useState<ReviewRequest | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchReviewRequest = async () => {
      if (!record?.id) return;

      try {
        const response = await dataProvider.getList('review_requests', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'created_at', order: 'DESC' },
          filter: { funeral_id: record.id },
        });

        if (response.data && response.data.length > 0) {
          setReviewRequest(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching review request:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchReviewRequest();
  }, [dataProvider, record?.id]);

  const handleToggle = async () => {
    if (!reviewRequest?.id) return;

    setUpdating(true);
    try {
      await dataProvider.update('review_requests', {
        id: reviewRequest.id,
        data: { enabled: !reviewRequest.enabled },
        previousData: reviewRequest,
      });

      setReviewRequest((prev) =>
        prev ? { ...prev, enabled: !prev.enabled } : null,
      );
      notify(
        reviewRequest.enabled
          ? 'Review request disabled'
          : 'Review request enabled',
        { type: 'success' },
      );
    } catch (error) {
      console.error('Error updating review request:', error);
      notify('Failed to update review request', { type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleSendNow = async () => {
    if (!reviewRequest?.id) return;

    setSending(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}review-requests/${reviewRequest.id}/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();

      if (response.ok && result.status === 'success') {
        setReviewRequest((prev) =>
          prev ? { ...prev, sent_at: new Date().toISOString() } : null,
        );
        notify('Review request sent', { type: 'success' });
      } else {
        notify(result.message || 'Failed to send review request', { type: 'error' });
      }
    } catch (error) {
      console.error('Error sending review request:', error);
      notify('Failed to send review request', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!reviewRequest) {
    return null;
  }

  const isSent = !!reviewRequest.sent_at;
  const scheduledDate = new Date(reviewRequest.scheduled_date);
  const sentDate = reviewRequest.sent_at
    ? new Date(reviewRequest.sent_at)
    : null;

  const getSendMethodLabel = (method: string) => {
    switch (method) {
      case 'sms':
        return 'SMS';
      case 'email':
        return 'Email';
      case 'both':
        return 'SMS & Email';
      default:
        return method;
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '3px',
            border: '2px solid',
            borderColor: isSent ? 'success.main' : reviewRequest.enabled ? 'info.main' : 'grey.400',
            bgcolor: isSent ? 'success.main' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isSent ? (
            <CheckCircleIcon sx={{ color: 'white', fontSize: 11 }} />
          ) : reviewRequest.enabled ? (
            <ScheduleIcon sx={{ color: 'info.main', fontSize: 11 }} />
          ) : null}
        </Box>
        <span className="text-[13px] font-medium text-muted-foreground">Review</span>
        {!isSent && (
          <>
            <Tooltip title="Send now">
              <IconButton
                onClick={handleSendNow}
                disabled={sending || updating}
                size="small"
                color="primary"
                sx={{ ml: 'auto', p: 0.5 }}
              >
                {sending ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={reviewRequest.enabled ? 'Disable review request' : 'Enable review request'}>
              <Switch
                checked={reviewRequest.enabled}
                onChange={handleToggle}
                disabled={updating}
                size="small"
                color="primary"
              />
            </Tooltip>
          </>
        )}
        {updating && <CircularProgress size={12} />}
      </div>
      <div className="pl-[22px] text-[12px] text-foreground leading-4 space-y-0.5">
        {isSent ? (
          <div className="text-green-600">
            Sent {sentDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} via {getSendMethodLabel(reviewRequest.send_method)}
          </div>
        ) : reviewRequest.enabled ? (
          <div>
            {scheduledDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} via {getSendMethodLabel(reviewRequest.send_method)}
          </div>
        ) : (
          <div className="text-muted-foreground">Disabled</div>
        )}
      </div>
    </div>
  );
};

// ─── PartnerVisibilityToggle ─────────────────────────────────────────────────

export const PartnerVisibilityToggle = () => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [visible, setVisible] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (record) {
      setVisible(record.visible_to_partners !== false);
    }
  }, [record?.visible_to_partners]);

  if (!record) return null;

  const handleToggle = async () => {
    const newValue = !visible;
    setVisible(newValue);
    setUpdating(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: { visible_to_partners: newValue },
        previousData: record,
      });
      notify(
        newValue
          ? 'Case visible to partners'
          : 'Case hidden from partners',
        { type: 'success' },
      );
    } catch (error) {
      console.error('Error updating partner visibility:', error);
      setVisible(!newValue);
      notify('Failed to update partner visibility', { type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Visible to Partners
      </Typography>
      <Tooltip
        title={
          visible
            ? 'Click to hide from partner portal'
            : 'Click to show on partner portal'
        }
      >
        <Switch
          checked={visible}
          onChange={handleToggle}
          disabled={updating}
          size="small"
          color="primary"
        />
      </Tooltip>
      {updating && <CircularProgress size={16} />}
    </Stack>
  );
};

// ─── DeliveryCard ────────────────────────────────────────────────────────────

const DELIVERY_METHOD_META: Record<
  string,
  { label: string; Icon: typeof Truck }
> = {
  shipping: { label: 'Shipping (Canada Post)', Icon: Truck },
  courier: { label: 'Courier (Courrier Plus)', Icon: Truck },
  office_pickup: { label: 'Pickup', Icon: Store },
  crematorium_pickup: { label: 'Pickup', Icon: Store },
  // Legacy, still on older cases: hand_delivery predates the courier flag, and
  // pickup predates the office/crematorium split.
  hand_delivery: { label: 'Hand Delivery', Icon: Hand },
  pickup: { label: 'Customer Pickup', Icon: Store },
};

const ASHES_OPTIONS: { value: string; label: string }[] = [
  { value: 'no-split', label: 'No Split' },
  { value: 'split-two', label: 'Split ×2' },
  { value: 'split-three', label: 'Split ×3' },
  { value: 'split-other', label: 'Split (Other)' },
];

/**
 * Replaces urn_type's two values, which could not say whether "custom" meant
 * the family bought our urn or is bringing their own.
 */
const URN_CHOICE_OPTIONS: { value: string; label: string }[] = [
  { value: 'cardboard', label: 'Cardboard (included)' },
  { value: 'cleo', label: 'Cleo display urn' },
  { value: 'family_own', label: "Family's own" },
];

const URN_COLOUR_OPTIONS: { value: string; label: string }[] = [
  { value: 'blue', label: 'Blue' },
  { value: 'beige', label: 'Beige' },
];

/** Where the supplier order has got to. The urn ships separately from the ashes. */
const URN_ORDER_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'to_order', label: 'To order' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'received', label: 'Received' },
];

const SELECTION_LABEL: Record<string, string> = {
  primary: 'Primary Client',
  authorized: 'Authorized Representative',
  payer: 'Payer',
  custom: 'Custom Address',
};

const formatDeliveryAddress = (delivery?: DeliveryAddress): string | null => {
  if (!delivery || (!delivery.address && !delivery.civic_number)) {
    return null;
  }
  const parts: string[] = [];
  const streetParts: string[] = [];
  if (delivery.apartment && delivery.civic_number) {
    streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
  } else if (delivery.apartment) {
    streetParts.push(delivery.apartment);
  } else if (delivery.civic_number) {
    streetParts.push(delivery.civic_number);
  }
  if (delivery.address) streetParts.push(delivery.address);
  if (streetParts.length > 0) parts.push(streetParts.join(' '));
  if (delivery.city) parts.push(delivery.city);
  if (delivery.province) parts.push(delivery.province);
  if (delivery.postal_code) parts.push(delivery.postal_code);
  return parts.join(', ');
};

// Inline, shadcn-based dropdown for editing a single funeral field directly
// from the sidebar. Shows the current label as the trigger (or a placeholder
// when unset) and persists the change immediately.
const InlineEditableSelect = ({
  field,
  value,
  options,
  placeholder,
}: {
  field:
    | 'split_ashes'
    | 'urn_type'
    | 'urn_choice'
    | 'urn_colour'
    | 'urn_order_status';
  value: string;
  options: { value: string; label: string }[];
  placeholder: string;
}) => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);

  if (!record) return null;

  const current = options.find((o) => o.value === value);

  const handleSelect = async (next: string) => {
    if (next === value) return;
    setSaving(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: { [field]: next || null },
        previousData: record,
      });
      notify('Updated', { type: 'success' });
      refresh();
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      notify('Update failed', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={saving}
          className={cn(
            'inline-flex items-center gap-0.5 rounded px-1 -ml-1 hover:bg-accent transition-colors disabled:opacity-50',
            current ? 'text-foreground' : 'text-amber-500',
          )}
        >
          {current ? current.label : placeholder}
          <ChevronDown className="w-3 h-3 text-muted-foreground/70 flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-[1300] min-w-[9rem]">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(v) => void handleSelect(v)}
        >
          {options.map((o) => (
            <DropdownMenuRadioItem
              key={o.value}
              value={o.value}
              className="text-[12px]"
            >
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// The crematorium reads this note off the partner portal and acts on it
// physically, so it is capped hard: long enough for "packaged urned for travel"
// or "split into a small bag", short enough to take in at a glance. The same
// 200 is enforced by a CHECK constraint on the column — see
// supabase/migrations/20260901120000_crematorium_ashes_note_limit.sql.
const CREMATORIUM_NOTE_MAX = 200;

// Free-text ashes-handling instruction for the crematorium. It sits directly
// under the Ashes row because that is where the decision it describes gets
// made, and it renders attached to that row rather than as one more field.
// Clicking opens a dialog; saving goes straight to the funeral, the same way
// InlineEditableSelect does.
const CrematoriumNoteRow = () => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  if (!record) return null;

  const note = (record.crematorium_special_instructions ?? '').trim();
  const trimmedDraft = draft.trim();
  const remaining = CREMATORIUM_NOTE_MAX - draft.length;
  const overLimit = remaining < 0;

  const openDialog = () => {
    setDraft(note);
    setOpen(true);
  };

  const handleSave = async () => {
    if (overLimit) return;
    if (trimmedDraft === note) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        // An all-whitespace note is no note. Store NULL rather than '', or the
        // partner page's truthiness check renders an empty highlight box.
        data: { crematorium_special_instructions: trimmedDraft || null },
        previousData: record,
      });
      notify(trimmedDraft ? 'Crematorium note saved' : 'Crematorium note cleared', {
        type: 'success',
      });
      setOpen(false);
      refresh();
    } catch (error) {
      console.error('Error updating crematorium note:', error);
      notify('Could not save the crematorium note', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {note ? (
        <button
          type="button"
          onClick={openDialog}
          className="w-full text-left rounded border-l-2 border-amber-400 bg-amber-50/60 dark:bg-amber-950/20 px-1.5 py-1 hover:bg-amber-100 dark:hover:bg-amber-950/40 transition-colors"
        >
          <span className="flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-500">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            For crematorium
            <Pencil className="w-2.5 h-2.5 ml-auto opacity-60 flex-shrink-0" />
          </span>
          <span className="block mt-0.5 break-words whitespace-pre-wrap text-foreground">
            {note}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          className="inline-flex items-center gap-1 rounded px-1 -ml-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Plus className="w-3 h-3 flex-shrink-0" />
          Note for crematorium
        </button>
      )}

      <Dialog open={open} onOpenChange={(next) => !saving && setOpen(next)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Note for the crematorium</DialogTitle>
            <DialogDescription>
              How the ashes should be handled — e.g. “packaged urned for travel”
              or “split into a small bag”. Shown to the crematorium on their
              schedule page.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            autoFocus
            value={draft}
            maxLength={CREMATORIUM_NOTE_MAX}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Packaged urned for travel"
            className="min-h-[96px]"
          />
          <div
            className={cn(
              'text-xs -mt-2',
              overLimit ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {remaining} characters left
          </div>

          <div className="flex justify-end gap-2">
            <Button size="small" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => void handleSave()}
              disabled={saving || overLimit}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Delivery confirmation ───────────────────────────────────────────────────

// REACT_APP_PAPERMAN_API_URL carries a trailing slash in some environments and
// not in others, and a doubled slash does not match the Nest route — so the
// separator is supplied here instead of being assumed either way.
const DELIVERY_API_BASE = (
  process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001'
).replace(/\/+$/, '');

/**
 * The staff-side counterpart to the family's delivery page: confirm on their
 * behalf, or take a confirmation back.
 *
 * Both actions go through the backend rather than writing delivery_confirmed_at
 * from here, because confirming is not one column — it moves the case status,
 * books the lane and messages the family, and only the service knows that whole
 * sequence.
 *
 * `record` arrives as a prop rather than from context so every hook below runs
 * unconditionally: DeliveryCard returns early when there is no record, and a
 * hook after that early return would be a different hook order per render.
 */
const DeliveryConfirmationControls = ({
  record,
  methodLabel,
  address,
  time,
  isDelivered,
}: {
  record: Funeral;
  methodLabel?: string;
  address: string | null;
  time: string | null;
  isDelivered: boolean;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [notifyFamily, setNotifyFamily] = useState(true);
  const [day, setDay] = useState(record.delivery_day ?? '');
  const [slot, setSlot] = useState(record.delivery_window ?? '');
  const [saving, setSaving] = useState(false);

  const isPickup =
    record.delivery_method === 'office_pickup' ||
    record.delivery_method === 'crematorium_pickup' ||
    record.delivery_method === 'pickup';

  // The same rule the backend resolves the lane by: a staff-set pickup wins,
  // otherwise the postal code decides. Mirrored here so the dialog asks for
  // exactly the fields the confirm will demand — staff arranging a slot on the
  // phone should not be sent to another screen to type it, and certainly not
  // told "please choose a window" by a dialog that never offered one.
  const postal = (record.delivery_address?.postal_code ?? '')
    .trim()
    .toUpperCase();
  // 'shipping' set by staff means Canada Post however Montréal the address is,
  // and Canada Post has no date or window to choose.
  const isCourier =
    !isPickup && record.delivery_method !== 'shipping' && postal.startsWith('H');
  const needsDay = isPickup || isCourier;
  const needsWindow = isCourier;

  // The earliest office pickup (T-230). The server refuses without a reason too.
  const [overrideReason, setOverrideReason] = useState('');
  const [allowEarlier, setAllowEarlier] = useState(false);
  const { plan } = useRemainsPlan(
    record.id,
    '',
    confirmOpen && record.delivery_method === 'office_pickup',
  );
  const dayRule =
    record.delivery_method === 'office_pickup' && plan?.officePickup?.reason
      ? plan.officePickup
      : null;
  const dayTooEarly = pickupDayTooEarly({
    method: record.delivery_method,
    day,
    savedDay: record.delivery_day,
    rule: dayRule,
  });

  // Reopening shows what is stored, not what was typed and abandoned last time.
  useEffect(() => {
    if (confirmOpen) {
      setDay(record.delivery_day ?? '');
      setSlot(record.delivery_window ?? '');
      setOverrideReason('');
      setAllowEarlier(false);
    }
  }, [confirmOpen, record.delivery_day, record.delivery_window]);

  // A pickup case has no delivery address — where the family collects is a
  // partner record, and it is the one detail the summary must not get wrong.
  const { data: pickupLocations = [] } = useGetList<Partner>(
    'partners',
    {
      filter: { is_ashes_pickup_location: true },
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: isPickup && !!record.pickup_location_id },
  );

  const pickupLocation = record.pickup_location_id
    ? pickupLocations.find(
        (p) => String(p.id) === String(record.pickup_location_id),
      )
    : undefined;
  const destination = isPickup ? pickupLocation?.name ?? null : address;

  const post = async (
    action: 'confirm' | 'unconfirm',
    body?: Record<string, unknown>,
  ) => {
    const response = await fetch(
      `${DELIVERY_API_BASE}/delivery-scheduling/staff/${record.id}/${action}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: body ? JSON.stringify(body) : undefined,
      },
    );
    if (!response.ok) {
      // Keep whatever the backend said: "no delivery date set" and "already
      // confirmed" are different problems, and staff can only act on the one
      // they actually hit.
      const raw = await response.text().catch(() => '');
      let detail = raw;
      try {
        const parsed = JSON.parse(raw) as { message?: string };
        detail = parsed.message ?? raw;
      } catch {
        // Not JSON — a proxy error page. Its raw text is the best we have.
      }
      throw new Error(detail || `Request failed (${response.status})`);
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await post('confirm', {
        notify: notifyFamily,
        ...(needsDay && day ? { day } : {}),
        ...(needsWindow && slot ? { window: slot } : {}),
        ...(dayTooEarly ? { overrideReason } : {}),
      });
      notify(
        notifyFamily ? 'Delivery confirmed, family notified' : 'Delivery confirmed',
        { type: 'success' },
      );
      setConfirmOpen(false);
      refresh();
    } catch (error) {
      // The dialog stays open: the choice is still on screen to retry or amend.
      notify(`Could not confirm the delivery: ${(error as Error).message}`, {
        type: 'error',
        autoHideDuration: 5000,
        multiLine: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelConfirmation = async () => {
    setSaving(true);
    try {
      await post('unconfirm');
      notify('Confirmation cancelled', { type: 'success' });
      setCancelOpen(false);
      refresh();
    } catch (error) {
      notify(`Could not cancel the confirmation: ${(error as Error).message}`, {
        type: 'error',
        autoHideDuration: 5000,
        multiLine: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmedAt = record.delivery_confirmed_at;

  if (confirmedAt) {
    const who =
      record.delivery_confirmed_by === 'family'
        ? 'Confirmed by the family'
        : 'Confirmed by staff';
    return (
      <>
        {/* One sentence, not a row with a button floated off the end of it:
            pushing the action to the right edge left it stranded on its own
            line as soon as the name and date filled the width. The year goes
            too — no other date on this card carries one. */}
        <div className="text-muted-foreground break-words">
          {who} · {moment(confirmedAt).format('MMM D')}
          {/* Once the ashes are gone there is no arrangement left to redo, so
              the case cannot be sent back to ready for delivery. */}
          {!isDelivered && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        <Dialog
          open={cancelOpen}
          onOpenChange={(next) => !saving && setCancelOpen(next)}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Cancel this confirmation?</DialogTitle>
              <DialogDescription>
                The date and window are kept, and the family is told nothing.
                The case goes back to ready for delivery so the arrangement can
                be made again.
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-end gap-2">
              <Button
                size="small"
                onClick={() => setCancelOpen(false)}
                disabled={saving}
              >
                Keep it
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => void handleCancelConfirmation()}
                disabled={saving}
              >
                {saving ? 'Cancelling…' : 'Cancel confirmation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (isDelivered) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className="inline-flex items-center gap-1 rounded px-1 -ml-1 text-amber-600 dark:text-amber-500 hover:bg-accent transition-colors"
      >
        <Check className="w-3 h-3 flex-shrink-0" />
        Confirm
      </button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(next) => !saving && setConfirmOpen(next)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm this delivery</DialogTitle>
            <DialogDescription>
              Records the arrangement as agreed, exactly as if the family had
              confirmed it themselves on their delivery page.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-border bg-card/50 p-2.5 text-[12px] text-foreground leading-4 space-y-1">
            <div className="break-words">
              <span className="text-muted-foreground">Method: </span>
              {methodLabel ?? 'Not set'}
            </div>
            <div className="break-words">
              <span className="text-muted-foreground">
                {isPickup ? 'Pickup at: ' : 'Address: '}
              </span>
              {destination ?? 'Not set'}
            </div>
            {needsDay ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-muted-foreground">When: </span>
                <input
                  type="date"
                  value={day}
                  min={dayRule && !allowEarlier ? dayRule.earliestDay : undefined}
                  onChange={(e) => setDay(e.target.value)}
                  className="rounded border border-input bg-transparent px-1.5 py-0.5 text-[12px]"
                />
                {needsWindow && (
                  <select
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                    className="rounded border border-input bg-transparent px-1.5 py-0.5 text-[12px]"
                  >
                    <option value="">Choose a window</option>
                    <option value="12pm-3pm">12pm - 3pm</option>
                    <option value="1pm-4pm">1pm - 4pm</option>
                    <option value="2pm-5pm">2pm - 5pm</option>
                  </select>
                )}
                {dayRule && (
                  <PickupDayRuleNote
                    rule={dayRule}
                    tooEarly={dayTooEarly}
                    allowEarlier={allowEarlier}
                    onAllowEarlier={() => setAllowEarlier(true)}
                    reason={overrideReason}
                    onReasonChange={setOverrideReason}
                  />
                )}
              </div>
            ) : (
              <div className="break-words">
                <span className="text-muted-foreground">When: </span>
                {time ?? 'Canada Post — no date to choose'}
              </div>
            )}
          </div>

          {/* Default on, because a confirmation the family never hears about is
              not one they can rely on. It comes off when staff are still
              negotiating and only need the agreement so far on the record. */}
          <label className="flex items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[#5e35b1]"
              checked={notifyFamily}
              onChange={(e) => setNotifyFamily(e.target.checked)}
            />
            <span>Send the family a confirmation</span>
          </label>
          {!notifyFamily && (
            <div className="-mt-1 text-[11px] text-muted-foreground">
              The family will not be messaged.
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              size="small"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => void handleConfirm()}
              disabled={saving || (dayTooEarly && !overrideReason.trim())}
            >
              {saving ? 'Confirming…' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * The ashes delivery's one status word, shared by the Delivery card and the
 * sidebar's Delivery section header so the two never disagree.
 */
export const deliveryStatus = (
  record: Funeral,
): { label: 'Delivered' | 'Scheduled' | 'Confirmed' | 'Not yet confirmed'; done: boolean } => {
  const delivery = record.delivery_address;
  // delivery_confirmed_at is the single definition of confirmed — types.ts says
  // so outright: never re-infer it from a day. Requiring one read "Not yet
  // confirmed" forever on the Canada Post lane, where the family confirms an
  // address and no day is ever offered, while the body of this same card said
  // "Confirmed by the family" three lines below it.
  //
  // The day and the xpresspost sentinel stay as a fallback for the cases that
  // predate the column — 342 of them still carry a day and no stamp.
  const isConfirmed = !!(
    record.delivery_confirmed_at ||
    (delivery?.address &&
      (record.delivery_day || record.delivery_window === 'xpresspost_signature'))
  );
  const isDelivered = !!record.delivered_at;

  // A confirmed shipping case has no schedule to speak of — no day is ever
  // chosen on that lane — so it says Confirmed rather than claiming one.
  return isDelivered
    ? { label: 'Delivered', done: true }
    : isConfirmed
      ? { label: record.delivery_day ? 'Scheduled' : 'Confirmed', done: true }
      : { label: 'Not yet confirmed', done: false };
};

export const DeliveryCard = () => {
  useFocusAnchor('delivery');
  const record = useRecordContext<Funeral>();

  if (!record) return null;

  const delivery = record.delivery_address;
  const method = record.delivery_method
    ? DELIVERY_METHOD_META[record.delivery_method]
    : undefined;

  const status = deliveryStatus(record);
  const isDelivered = !!record.delivered_at;

  const formatTime = (): string | null => {
    // The day stands on its own. Requiring a window beside it meant a case with
    // a date and no window — every pickup, and any courier case before the
    // window is chosen — read as "Not set" while the date sat in the database.
    if (record.delivery_day) {
      const [y, m, d] = record.delivery_day.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      const formatted = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      return record.delivery_window &&
        record.delivery_window !== 'xpresspost_signature'
        ? `${formatted} · ${record.delivery_window}`
        : formatted;
    }
    // A pickup saved before the family knows when (T-175).
    if (isPickupMethod(record.delivery_method)) return PICKUP_DATE_TBD;
    if (record.delivery_window === 'xpresspost_signature') {
      return 'Xpresspost + Signature';
    }
    if (record.delivery_window) return record.delivery_window;
    if (record.delivery_date) {
      return moment(record.delivery_date).format('MMM D, YYYY');
    }
    return null;
  };

  const time = formatTime();
  const address = formatDeliveryAddress(delivery);
  const selectionLabel = delivery?.selection
    ? SELECTION_LABEL[delivery.selection] ?? delivery.selection
    : null;

  return (
    <div
      data-notes-anchor="delivery"
      className="rounded-md border border-border bg-card/50 p-2.5 space-y-2 min-w-0 overflow-hidden"
    >
      {/* Header: status + edit */}
      <div className="flex items-center gap-1.5 min-w-0">
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '3px',
            border: '2px solid',
            borderColor: status.done ? 'success.main' : 'grey.400',
            bgcolor: status.done ? 'success.main' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {status.done && (
            <Box
              component="span"
              sx={{ color: 'white', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}
            >
              ✓
            </Box>
          )}
        </Box>
        <span className="text-[13px] font-medium text-foreground flex-shrink-0">Delivery</span>
        <span
          className={`text-[11px] font-medium truncate min-w-0 ${
            status.done ? 'text-muted-foreground' : 'text-amber-500'
          }`}
        >
          · {status.label}
        </span>
        <div className="ml-auto flex items-center gap-0.5 flex-shrink-0">
          <DeliveryEditButton
            sx={{ minWidth: 0, padding: '2px 6px' }}
            icon={<Pencil className="w-3.5 h-3.5" />}
          />
        </div>
      </div>

      {/* Detail */}
      <div className="text-[12px] text-foreground leading-4 space-y-1.5">
        {method && (
          <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
            <method.Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="break-words">{method.label}</span>
          </div>
        )}
        {address && <div className="break-words">{address}</div>}
        {time && <div className="text-muted-foreground">{time}</div>}

        {/* Directly under the date, because confirming is a statement about
            that date and no other field on this card. */}
        <DeliveryConfirmationControls
          record={record}
          methodLabel={method?.label}
          address={address}
          time={time}
          isDelivered={isDelivered}
        />

        {/* Recipient → Ashes */}
        {delivery?.recipient_name ? (
          <div>
            <span className="text-muted-foreground">Recipient: </span>
            {delivery.recipient_name}
            {selectionLabel && (
              <span className="text-muted-foreground"> · {selectionLabel}</span>
            )}
          </div>
        ) : selectionLabel ? (
          <div>
            <span className="text-muted-foreground">Recipient: </span>
            {selectionLabel}
          </div>
        ) : null}
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-muted-foreground flex-shrink-0">Ashes: </span>
          <InlineEditableSelect
            field="split_ashes"
            value={record.split_ashes ?? ''}
            options={ASHES_OPTIONS}
            placeholder="Not yet confirmed"
          />
        </div>
        {/* Directly under the Ashes row, because the note is about how these
            ashes get packaged for the journey. Full width rather than inline
            beside the value: the sidebar is 384px on desktop and narrower
            still in the mobile drawer (85vw, max 360px), which would truncate
            a 200-char note to a useless fragment. */}
        <CrematoriumNoteRow />

        {delivery?.recipient_phone && (
          <div>
            <span className="text-muted-foreground">Phone: </span>
            {delivery.recipient_phone}
          </div>
        )}
        {record.delivery_note && (
          <div>
            <span className="text-muted-foreground">Note: </span>
            {record.delivery_note}
          </div>
        )}
        {record.shipping_tracking_number && (
          <div>
            <span className="text-muted-foreground">Tracking: </span>
            {record.shipping_label_url ? (
              <a
                href={record.shipping_label_url}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                {record.shipping_tracking_number}
              </a>
            ) : (
              record.shipping_tracking_number
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── UrnCard ─────────────────────────────────────────────────────────────────

// The urn's tracking number was only reachable from the cremation tracker's
// "Delivery Scheduled" screen — the ashes' screen, not the urn's, and closed to
// anyone whose case sits at a different status. It belongs on the urn card,
// beside the order it belongs to. Same click-to-dialog pattern as the
// crematorium note.
const UrnTrackingRow = () => {
  const record = useRecordContext<Funeral>();
  const [open, setOpen] = useState(false);

  if (!record) return null;

  const tracking = (record.urn_tracking_number ?? '').trim();
  const openDialog = () => setOpen(true);

  return (
    <>
      {tracking ? (
        <button
          type="button"
          onClick={openDialog}
          className="w-full flex items-center gap-1 min-w-0 rounded px-1 -ml-1 text-left hover:bg-accent transition-colors"
        >
          <span className="text-muted-foreground flex-shrink-0">Tracking: </span>
          <span className="truncate">{tracking}</span>
          <Pencil className="w-2.5 h-2.5 ml-auto opacity-60 flex-shrink-0" />
        </button>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          className="inline-flex items-center gap-1 rounded px-1 -ml-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Plus className="w-3 h-3 flex-shrink-0" />
          Tracking number
        </button>
      )}

      {/* The same dialog the "Order Urn" timeline step opens. Ordering the urn
          and recording what came back are one job, so they are one screen. */}
      <UrnOrderDialog record={record} open={open} onOpenChange={setOpen} />
    </>
  );
};

/**
 * "Add $300 to the contract?" — the step that used to be somebody remembering.
 *
 * A family who chooses our urn is shown "$300 + tax" and is billed only if
 * staff add the line by hand afterwards. On production 14 of 37 cases marked
 * 'custom' carry no urn line at all. Onboarding now writes the line itself for
 * new cases; this covers the rest — a case staff set by hand, or one where the
 * family changed their mind after the contract was drawn up.
 *
 * It writes the same shape a hand-entered line carries, takes price and tax
 * code from the QuickBooks item rather than inventing them, and re-syncs the
 * estimate exactly as the contract Save button does — without that POST the
 * contract's total still describes the old line set.
 */
const AddUrnToContractRow = () => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const urnItem =
    record?.region?.toLowerCase() === 'ontario'
      ? URN_ITEMS.ontario
      : URN_ITEMS.quebec;

  const { data: contracts } = useGetList(
    'contracts',
    {
      filter: { funeral_id: record?.id },
      pagination: { page: 1, perPage: 5 },
      sort: { field: 'id', order: 'DESC' },
    },
    { enabled: !!record?.id },
  );

  const { data: items } = useGetMany(
    'items',
    { ids: [urnItem ?? ''] },
    { enabled: !!urnItem },
  );

  if (!record) return null;

  // Ontario has no urn item yet. Say so rather than offering a button that
  // would bill HST-province families at Quebec rates.
  if (!urnItem) {
    return (
      <div className="text-muted-foreground">
        No Ontario urn item in QuickBooks yet — add it there first.
      </div>
    );
  }

  const contract = contracts?.[0];
  const lines: Record<string, unknown>[] = Array.isArray(contract?.lines)
    ? (contract?.lines as Record<string, unknown>[])
    : [];
  const alreadyOnContract = lines.some(
    (line) => String(line?.item ?? '') === urnItem,
  );

  if (!contract) {
    return <div className="text-muted-foreground">No contract yet</div>;
  }

  if (alreadyOnContract) {
    return (
      <div className="text-muted-foreground">On the contract ✓</div>
    );
  }

  const item = items?.[0] as
    | { Name?: string; Description?: string; UnitPrice?: number; SalesTaxCodeRef?: { value?: string } }
    | undefined;
  const price = Number(item?.UnitPrice ?? 0);
  const tax = item?.SalesTaxCodeRef?.value;
  const colour = record.urn_colour
    ? record.urn_colour === 'blue'
      ? 'Blue'
      : 'Beige'
    : null;
  // contracts_signed_at lives on the funeral, written by the DocuSeal webhook.
  // The contract's own signature columns are null on every row in production.
  const signed = !!record.contracts_signed_at;

  const handleAdd = async () => {
    // QuickBooks rejects an estimate whose line carries no tax code, and the
    // contract save refuses to sync at all -- so a line without one would take
    // the whole contract down with it, not just the urn.
    if (!tax) {
      notify('That urn item has no tax code in QuickBooks, so the contract could not sync', {
        type: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      const line = {
        item: urnItem,
        name: item?.Name ?? 'Cleo Urne',
        // The colour rides in the description rather than in a separate item:
        // both colours share a price, a tax code and an account, so a second
        // SKU would track nothing QuickBooks is not already tracking.
        description: colour
          ? `${item?.Description ?? item?.Name ?? 'Urne Cleo'} — ${colour}`
          : (item?.Description ?? item?.Name ?? 'Urne Cleo'),
        price,
        quantity: 1,
        total: price,
        tax,
        type: 'Goods and Services',
      };

      await dataProvider.update('contracts', {
        id: contract.id,
        data: { lines: [...lines, line] },
        previousData: contract,
      });

      // Same call the contract Save button makes. QuickBooks owns the
      // arithmetic: subtotal, taxes and total_amount come back from it.
      const synced = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}quickbooks/estimates/${contract.id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            'Refresh-Token': await getRefreshToken(),
          },
        },
      );

      if (!synced.ok) {
        notify(
          'Urn added to the contract, but QuickBooks did not re-price it — open the contract and save it',
          { type: 'warning', autoHideDuration: 8000 },
        );
      } else {
        notify(`Cleo urn added to the contract ($${price})`, { type: 'success' });
      }

      // On the case feed, because a signed total that changed is something a
      // person has to act on: the money still has to be collected.
      await dataProvider.create('customer_events', {
        data: {
          funeral_id: Number(record.id),
          timestamp: new Date().toISOString(),
          type: CustomerEventType.INTERNAL_NOTE,
          message: signed
            ? `Cleo urn ($${price}) added to contract ${contract.id} after signing — collect payment.`
            : `Cleo urn ($${price}) added to contract ${contract.id}.`,
        },
      });

      setOpen(false);
      refresh();
    } catch (error) {
      console.error('Error adding the urn to the contract:', error);
      notify('Could not add the urn to the contract', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded px-1 -ml-1 text-amber-600 dark:text-amber-500 hover:bg-accent transition-colors"
      >
        <Plus className="w-3 h-3 flex-shrink-0" />
        Not on the contract — add it
      </button>

      <Dialog open={open} onOpenChange={(next) => !saving && setOpen(next)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add the urn to the contract?</DialogTitle>
            <DialogDescription>
              {colour ? `Cleo urn (${colour})` : 'Cleo urn'}
              {` at $${price}, taxed as QuickBooks has it for this province.`}
              {signed
                ? ' This contract is already signed, so the total changes after the fact — the case feed will say so, and the money still has to be collected.'
                : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end gap-2">
            <Button size="small" onClick={() => setOpen(false)} disabled={saving}>
              Not now
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => void handleAdd()}
              disabled={saving}
            >
              {saving ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * The urn is its own process, not a delivery field.
 *
 * It is chosen by the family, ordered from a supplier by us, and shipped on its
 * own schedule with its own tracking number — none of which moves in step with
 * the ashes. Inside the delivery card these rows read as delivery state, and an
 * urn marked "Shipped" looked like the ashes were on their way.
 */
export const UrnCard = () => {
  const record = useRecordContext<Funeral>();

  if (!record) return null;

  const choice = record.urn_choice ?? null;
  const isCleo = choice === 'cleo';
  const orderLabel =
    URN_ORDER_STATUS_OPTIONS.find((o) => o.value === record.urn_order_status)
      ?.label ?? 'To order';

  // Anything but our own urn is finished the moment it is chosen: the cardboard
  // box is included, and a family's own urn is already in their hands. Only a
  // Cleo urn has an order to chase, so only it carries the order ladder.
  const status = !choice
    ? { label: 'Not yet chosen', done: false }
    : !isCleo
      ? {
          label: choice === 'cardboard' ? 'Cardboard' : "Family's own",
          done: true,
        }
      : { label: orderLabel, done: record.urn_order_status === 'received' };

  return (
    <div className="rounded-md border border-border bg-card/50 p-2.5 space-y-2 min-w-0 overflow-hidden">
      {/* Header: status */}
      <div className="flex items-center gap-1.5 min-w-0">
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '3px',
            border: '2px solid',
            borderColor: status.done ? 'success.main' : 'grey.400',
            bgcolor: status.done ? 'success.main' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {status.done && (
            <Box
              component="span"
              sx={{ color: 'white', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}
            >
              ✓
            </Box>
          )}
        </Box>
        <span className="text-[13px] font-medium text-foreground flex-shrink-0">Urn</span>
        <span
          className={`text-[11px] font-medium truncate min-w-0 ${
            status.done ? 'text-muted-foreground' : 'text-amber-500'
          }`}
        >
          · {status.label}
        </span>
      </div>

      {/* Detail */}
      <div className="text-[12px] text-foreground leading-4 space-y-1.5">
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-muted-foreground flex-shrink-0">Chosen: </span>
          <InlineEditableSelect
            field="urn_choice"
            value={record.urn_choice ?? ''}
            options={URN_CHOICE_OPTIONS}
            // Older cases carry only urn_type, which is not being backfilled —
            // show what it said so staff can confirm rather than re-guess.
            placeholder={
              record.urn_type
                ? `${record.urn_type === 'custom' ? 'Custom' : 'Standard'} (old field)`
                : 'Not yet confirmed'
            }
          />
        </div>
        {/* Colour and the supplier order only mean anything for our own urn. */}
        {isCleo && (
          <>
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-muted-foreground flex-shrink-0">Colour: </span>
              <InlineEditableSelect
                field="urn_colour"
                value={record.urn_colour ?? ''}
                options={URN_COLOUR_OPTIONS}
                placeholder="Not chosen"
              />
            </div>
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-muted-foreground flex-shrink-0">Supplier order: </span>
              <InlineEditableSelect
                field="urn_order_status"
                value={record.urn_order_status ?? ''}
                options={URN_ORDER_STATUS_OPTIONS}
                placeholder="To order"
              />
            </div>
            {/* The supplier ships the urn to the family directly, so this
                number is the urn's, not the ashes' tracking. */}
            <UrnTrackingRow />
            {/* Whether anyone is actually being billed for it. */}
            <AddUrnToContractRow />
          </>
        )}
      </div>
    </div>
  );
};

// ─── CremationPartnersDisplay ────────────────────────────────────────────────

const CremationPartnersReadOnly = () => {
  const record = useRecordContext();

  if (
    !record ||
    (!record.transport_partner_id && !record.crematorium_partner_id)
  ) {
    return <Typography>Not Set</Typography>;
  }

  if (!record.transport_partner_id || !record.crematorium_partner_id) {
    return (
      <>
        {record.transport_partner_id && (
          <ReferenceField
            source="transport_partner_id"
            reference="partners"
            label="Transport Company"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        )}

        {record.crematorium_partner_id && (
          <ReferenceField
            source="crematorium_partner_id"
            reference="partners"
            label="Crematorium"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        )}
      </>
    );
  }

  // A distinct drop-off (set and different from the crematorium) makes the
  // arrow chain three names long and cramped — switch to labeled rows instead.
  const hasDistinctDropoff =
    !!record.dropoff_partner_id &&
    String(record.dropoff_partner_id) !== String(record.crematorium_partner_id);

  if (hasDistinctDropoff) {
    const rows: Array<{ label: string; source: string }> = [
      { label: 'Transport', source: 'transport_partner_id' },
      { label: 'Drop-off', source: 'dropoff_partner_id' },
      { label: 'Crematorium', source: 'crematorium_partner_id' },
    ];
    return (
      <Stack spacing={0.5}>
        {rows.map((row) => (
          <Fragment key={row.source}>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="caption" color="text.secondary" sx={{ width: 84, flexShrink: 0 }}>
                {row.label}
              </Typography>
              <ReferenceField source={row.source} reference="partners" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </Stack>
            {/* The internal handoff only exists between these two names, so it
                reads as a note on the drop-off row rather than a row of its own. */}
            {row.source === 'dropoff_partner_id' && (
              <>
                <Typography
                  variant="caption"
                  color={record.dropoff_released_at ? 'text.secondary' : 'warning.main'}
                  sx={{ pl: '92px' }}
                >
                  {record.dropoff_released_at
                    ? `Released to crematorium · ${moment(record.dropoff_released_at).format('MMM D, h:mm A')}${
                        record.dropoff_released_by ? ` · ${record.dropoff_released_by}` : ''
                      }`
                    : 'Awaiting release'}
                </Typography>
                {/* What the facility did to the body and what it will bill us
                    for. Both are recorded from the partner portal, and both are
                    absent on almost every case — so they only appear when true,
                    unlike the release line above. */}
                {record.pacemaker_removed_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: '92px' }}>
                    {`Pacemaker removed · ${moment(record.pacemaker_removed_at).format('MMM D')}${
                      record.pacemaker_removed_by ? ` · ${record.pacemaker_removed_by}` : ''
                    }`}
                  </Typography>
                )}
                {!!record.dropoff_storage_days && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: '92px' }}>
                    {`${record.dropoff_storage_days} extra storage ${
                      record.dropoff_storage_days === 1 ? 'day' : 'days'
                    } claimed`}
                  </Typography>
                )}
              </>
            )}
          </Fragment>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <FunctionField
        render={() => (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReferenceField
              source="transport_partner_id"
              reference="partners"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <Typography> → </Typography>
            <ReferenceField
              source="crematorium_partner_id"
              reference="partners"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Stack>
        )}
      />
    </Stack>
  );
};

export const CremationPartnersDisplay = () => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [editing, setEditing] = useState(false);
  const [transport, setTransport] = useState('');
  const [crematorium, setCrematorium] = useState('');
  // '' = drop the body off at the crematorium (dropoff_partner_id stays null)
  const [dropoff, setDropoff] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: partnersList, isLoading: isLoadingPartners } = useGetList(
    'partners',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
  );

  // Role capability + market: an ON case only offers ON partners, and vice versa.
  const regionPartners = (partnersList || []).filter((p) =>
    partnerMatchesRegion(p as Partner, record?.region),
  );
  const transportPartners = regionPartners.filter((p) => p.is_transport);
  const crematoriumPartners = regionPartners.filter((p) => p.is_crematorium);
  const dropoffPartners = regionPartners.filter((p) => p.is_dropoff_location);

  const startEditing = () => {
    setTransport(record?.transport_partner_id?.toString() ?? '');
    setCrematorium(record?.crematorium_partner_id?.toString() ?? '');
    setDropoff(record?.dropoff_partner_id?.toString() ?? '');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!record) return;

    // Warn before changing a crematorium that's already set once the agreement has been
    // sent — the signed cremation agreement is crematorium-specific, and transport
    // dispatch emails whichever crematorium is set here.
    const currentCrematorium = record.crematorium_partner_id?.toString() ?? '';
    const crematoriumChanged = (crematorium || '') !== currentCrematorium;
    const agreementSent = !!record.contracts_sent_at;
    if (crematoriumChanged && currentCrematorium && agreementSent) {
      const currentName =
        crematoriumPartners.find((p) => p.id?.toString() === currentCrematorium)?.name ??
        'the current crematorium';
      const proceed = window.confirm(
        `The cremation agreement for this case has already been sent for ${currentName}. ` +
          `Changing the crematorium may send the family to a crematorium that does not match ` +
          `the agreement they signed. Change it anyway?`,
      );
      if (!proceed) return;
    }

    setSaving(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: {
          transport_partner_id: transport || null,
          crematorium_partner_id: crematorium || null,
          dropoff_partner_id: dropoff || null,
        },
        previousData: record,
      });
      notify('Partners updated', { type: 'success' });
      setEditing(false);
      refresh();
    } catch (error) {
      console.error('Error updating partners:', error);
      notify('Failed to update partners', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  if (!editing) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CremationPartnersReadOnly />
        <Tooltip title="Edit partners">
          <IconButton size="small" onClick={startEditing}>
            <Pencil className="w-3.5 h-3.5" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
      <FormControl fullWidth size="small">
        <InputLabel id="sidebar-transport-partner-label">
          Transport Partner
        </InputLabel>
        <Select
          labelId="sidebar-transport-partner-label"
          value={transport}
          label="Transport Partner"
          onChange={(e) => setTransport(e.target.value as string)}
          disabled={isLoadingPartners || saving}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {transportPartners.map((partner) => (
            <MenuItem key={partner.id} value={partner.id.toString()}>
              {partner.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel id="sidebar-crematorium-partner-label">
          Crematorium
        </InputLabel>
        <Select
          labelId="sidebar-crematorium-partner-label"
          value={crematorium}
          label="Crematorium"
          onChange={(e) => setCrematorium(e.target.value as string)}
          disabled={isLoadingPartners || saving}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {crematoriumPartners.map((partner) => (
            <MenuItem key={partner.id} value={partner.id.toString()}>
              {partner.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Ontario only for now: the body can be dropped off for storage/prep at a
          facility other than the crematorium. Empty = same as crematorium. */}
      {record.region === 'ontario' && (
        <FormControl fullWidth size="small">
          <InputLabel id="sidebar-dropoff-partner-label" shrink>
            Drop-off location
          </InputLabel>
          <Select
            labelId="sidebar-dropoff-partner-label"
            value={dropoff}
            label="Drop-off location"
            onChange={(e) => setDropoff(e.target.value as string)}
            disabled={isLoadingPartners || saving}
            displayEmpty
            notched
          >
            <MenuItem value="">
              <em>Same as crematorium</em>
            </MenuItem>
            {dropoffPartners.map((partner) => (
              <MenuItem key={partner.id} value={partner.id.toString()}>
                {partner.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          Save
        </Button>
        <Button
          size="small"
          onClick={() => setEditing(false)}
          disabled={saving}
        >
          Cancel
        </Button>
        {saving && <CircularProgress size={16} />}
      </Stack>
    </Stack>
  );
};

// ─── SpouseField ─────────────────────────────────────────────────────────────

export const SpouseField = (props: TextFieldProps) => {
  const record = useRecordContext();

  return (
    <ReferenceField source="deceased_client_id" reference="clients">
      <FunctionField
        render={(record: any) => {
          const spouseName = record.spouse_id ? (
            <ReferenceField
              source="spouse_id"
              reference="clients"
              link={false}
            >
              <FunctionField
                render={(spouseRecord: any) => (
                  <EditInDialogButton
                    fullWidth
                    maxWidth="md"
                    sx={{ padding: 4 }}
                    label={spouseRecord.name}
                  >
                    <SimpleForm toolbar={<ClientSaveToolbar />}>
                      <ClientEditBase />
                    </SimpleForm>
                  </EditInDialogButton>
                )}
              />
            </ReferenceField>
          ) : null;

          const legacySpouse = record.legacy_spouse
            ? ` ${record.legacy_spouse}`
            : '';
          return (
            <>
              {spouseName}
              {legacySpouse}
            </>
          );
        }}
      />
    </ReferenceField>
  );
};

// ─── ObitField ───────────────────────────────────────────────────────────────

export const ObitField = (props: TextFieldProps) => {
  const record = useRecordContext();
  if (record.obituary_en?.length > 0) {
    return <Typography>Yes</Typography>;
  } else {
    return <Typography>No</Typography>;
  }
};

// ─── NonPapermanChip ─────────────────────────────────────────────────────────

export const NonPapermanChip = () => {
  const record = useRecordContext<Funeral>();

  if (record.non_paperman_service) {
    const text = 'Non Paperman Funeral';
    const color = '#ffeaba';

    return (
      <Chip
        label={text}
        sx={{ marginLeft: '10px', backgroundColor: color }}
      />
    );
  }

  return null;
};
